/**
 * 국세청 사업자 진위확인 API 클라이언트
 *
 * 백엔드 엔드포인트: POST /api/v1/verify-business
 * Firebase Hosting의 rewrites 설정으로 Cloud Run으로 자동 라우팅됨.
 *
 * 응답 status 값:
 * - 'valid_only' : 국세청 등록된 정상 사업자 (계속사업자/휴업자)
 * - 'closed'     : 폐업 사업자 (경고 후 진행 권장)
 * - 'not_found'  : 등록되지 않은 사업자번호 (가짜 가능성, 차단 권장)
 * - 'unknown'    : API 장애 등 검증 불가 (검증 우회, 진행 권장)
 */

export interface VerifyBusinessResponse {
  status: 'valid' | 'valid_only' | 'mismatch' | 'not_found' | 'closed' | 'unknown';
  business_status?: string | null;
  tax_type?: string | null;
  message: string;
  mismatched_fields?: string[];
}

/**
 * 사업자번호 진위확인 API 호출
 *
 * @param businessNumber - 하이픈 포함/미포함 모두 허용 (10자리)
 * @returns 검증 결과. 네트워크 오류 시에도 status='unknown'으로 fallback
 */
export async function verifyBusinessNumber(
  businessNumber: string,
): Promise<VerifyBusinessResponse> {
  try {
    const response = await fetch('/api/v1/verify-business', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ business_number: businessNumber }),
    });

    if (!response.ok) {
      // 4xx, 5xx 응답 시 unknown 처리 (검증 우회)
      console.warn('[verifyBusinessNumber] HTTP error:', response.status);
      return {
        status: 'unknown',
        message: '검증 서버 오류로 확인하지 못했습니다.',
      };
    }

    const data = (await response.json()) as VerifyBusinessResponse;
    return data;
  } catch (err) {
    // 네트워크 오류, 타임아웃 등
    console.warn('[verifyBusinessNumber] Network error:', err);
    return {
      status: 'unknown',
      message: '검증 중 네트워크 오류가 발생했습니다.',
    };
  }
}
